import { useUser } from "@clerk/clerk-react";
import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { AdminScopeProvider } from "@/contexts/AdminScopeContext";

import { effectiveAdminRole } from "@/lib/roles";
import DashboardPage from "@/pages/DashboardPage";

/** Where an assessor is sent when they land on an admin route they can't use. */
export const ASSESSOR_HOME = "/admin/reviews";

/**
 * Gate for admin-portal routes. `allowedRoles` defaults to admin-only; pass a
 * wider set (e.g. ["admin", "assessor"]) for shared routes like the review
 * queue. An assessor who hits an admin route they're not allowed on is bounced
 * to their admin home (the review queue) rather than out to the participant
 * app; everyone else goes to "/". The participant app itself is open to
 * assessors — the role only limits the admin surface.
 */
export function AdminRoute({
  children,
  allowedRoles = ["admin"],
}: {
  children: ReactNode;
  allowedRoles?: string[];
}) {
  const { user, isLoaded } = useUser();

  if (!isLoaded) {
    return null;
  }

  // Superadmin passes any gate that admits admin (superset role).
  const role = effectiveAdminRole(user?.publicMetadata?.role as string | undefined);

  if (role && allowedRoles.includes(role)) {
    // The shared cohort/assignment scope must wrap the page component itself
    // (pages call useAdminScope in their body, above the AdminLayout they
    // render), so it lives here rather than in AdminLayout.
    return <AdminScopeProvider>{children}</AdminScopeProvider>;
  }

  if (role === "assessor") {
    return <Navigate to={ASSESSOR_HOME} replace />;
  }

  return <Navigate to="/" replace />;
}

/**
 * The app root ("/") is the participant dashboard for every role — assessors
 * included, since they can participate in programmes like any user. They reach
 * their review queue via the account menu (see TopBar).
 */
export function RoleHome() {
  const { isLoaded } = useUser();

  if (!isLoaded) {
    return null;
  }

  return <DashboardPage />;
}
